/**
 * Cell-calculation strategy switcher for the Plot Matrix (JSON) tab — same
 * concept and option set as `PlotMatrix.tsx`'s own `CELL_CALC_STRATEGY_OPTIONS`
 * / `renderCellCalcStrategySwitcher` (and `GroundworksReview.tsx`'s own copy
 * for the toolbar next to the live embedded Plot Matrix). Kept as its own
 * local copy rather than an import — same precedent those two render sites
 * already follow — since `PlotMatrix.tsx` doesn't export the array.
 */
import React, { useState } from "react";
import { Radio, Space, Modal, Button } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { CellCalcStrategy } from "../../../../utils/plotMatrixCategoryGenerator";

const CELL_CALC_STRATEGY_OPTIONS: ReadonlyArray<{
  value: CellCalcStrategy;
  label: string;
  shortLabel: string;
  info: string;
}> = [
  {
    value: "proportional",
    label: "Option A — Proportional Bill Summary share",
    shortLabel: "Option A",
    info:
      "Cell  = (rowQty / totalQty) × billSummaryAmount,\n" +
      "         where billSummaryAmount = round(|Math.round(totalQty) × rate|, 2).\n" +
      "Column total = Σ cells in the column.\n" +
      "Row total    = Σ cells in the row.\n" +
      "Grand total  = Σ column totals = Σ row totals.\n\n" +
      "Each row's cell is its proportional share of the field's rounded-qty BoQ amount. `rate` is the 2dp-rounded item-library rate — the same value the estimator sees in the build-up rate cells and the Bill Summary rate column. Current production behavior.",
  },
  {
    value: "rawRowQty",
    label: "Option B — Raw row quantity × rate",
    shortLabel: "Option B",
    info:
      "Cell  = rowQty × rate (signed, no qty rounding).\n" +
      "Column total = Σ rowQty × rate.\n" +
      "Row total    = Σ rowQty × rate.\n" +
      "Grand total  = Σ column totals = Σ row totals.\n\n" +
      "Literal per-row math: cell = qty × visible rate. Example: qty 3, rate 1.35 → cell = 4.05. `rate` is the 2dp-rounded item-library rate, so the cell value matches what the estimator can verify by hand against the Bill Summary rate column.",
  },
];

export interface PlotMatrixJsonCellCalcSwitcherProps {
  value: CellCalcStrategy;
  onChange: (value: CellCalcStrategy) => void;
}

const PlotMatrixJsonCellCalcSwitcher: React.FC<PlotMatrixJsonCellCalcSwitcherProps> = ({
  value,
  onChange,
}) => {
  const [infoIdx, setInfoIdx] = useState<number | null>(null);

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 8px",
          border: "1px dashed #d1d5db",
          borderRadius: 4,
          background: "#fafafa",
        }}
      >
        <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>
          Cell calc:
        </span>
        <Radio.Group
          value={value}
          onChange={(e) => onChange(e.target.value as CellCalcStrategy)}
          size="small"
        >
          <Space size={4}>
            {CELL_CALC_STRATEGY_OPTIONS.map((opt, idx) => (
              <Radio key={opt.value} value={opt.value}>
                <span style={{ fontSize: 12 }}>{opt.shortLabel}</span>
                <InfoCircleOutlined
                  style={{ marginLeft: 4, color: "#1677ff", fontSize: 12, cursor: "pointer" }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setInfoIdx(idx);
                  }}
                />
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </div>

      <Modal
        title={infoIdx !== null ? CELL_CALC_STRATEGY_OPTIONS[infoIdx].label : ""}
        open={infoIdx !== null}
        onCancel={() => setInfoIdx(null)}
        footer={[
          <Button key="close" onClick={() => setInfoIdx(null)}>
            Close
          </Button>,
        ]}
        width={620}
      >
        {infoIdx !== null && (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: 13,
              lineHeight: 1.5,
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 4,
              padding: 12,
              margin: 0,
            }}
          >
            {CELL_CALC_STRATEGY_OPTIONS[infoIdx].info}
          </pre>
        )}
      </Modal>
    </>
  );
};

export default PlotMatrixJsonCellCalcSwitcher;
